import React, { PureComponent } from "react";
import moment from "moment";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { XRayTooltip } from "../Utils/XrayUtils.js";

import {
  Metric,
  StandardTooltip,
  CustomizedXAxisTickTimeDay,
  CustomizedYAxisTick,
  CustomizedYAxisTickRightCurrency,
  selectFormat,
  Error,
  MAIN_COLORS,
} from "components/Utils/Utils.js";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  closeAllBarchartTooltips,
} from "components/Utils/InsightsUtils.js";

import { getXrayTooltipPosition } from "helpers/DOM";

const widgetConfig = {
  new_subscriptions: {
    label: "Evergent New Subscriptions",
    type: "number",
  },
  total_purchases: {
    label: "Ad Total Purchases",
    type: "number",
  },
  total_spend: {
    label: "Total Spend",
    type: "currency",
  },
  cpa: {
    label: "Ad Network CPA",
    type: "currency",
  },
  total_starts_cpa: {
    label: "Total Starts CPA",
    type: "currency",
  },
};

class RenderMetric extends PureComponent {
  render() {
    const { data, type, label, format, size } = this.props;
    const columnClass = "col-6 col-md-3 mb-3";

    return (
      <div className={columnClass}>
        <h6 className="widget__metric-label">{label}</h6>
        <Metric {...data} size={size} type={type} format={format} />
      </div>
    );
  }
}

class GothamPurchasesCPA extends PureComponent {
  constructor(props) {
    super(props);
    this.node = React.createRef();

    this.state = {
      widgetsData: props.data?.data || {},
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    this.closeTooltip();
    if (!this.chartRef || !this.chartRef.state) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  getExportData = () => {
    const { data } = this.props;

    if (!data?.graph?.length) {
      return {
        data: [],
        opts: { type: "other" },
      };
    }

    const chartData = data.graph.map((point) => {
      return {
        Date: point.date,
        "Evergent New Subscriptions": point.new_subscriptions || 0,
        "Ad Total Purchases": point.total_purchases || 0,
        "Ad Network CPA": point.cpa || 0,
        "Total Starts CPA": point.total_starts_cpa || 0,
      };
    });

    return {
      data: chartData,
      opts: {
        type: "other",
      },
    };
  };

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    if (
      this.divPosition.current &&
      this.divPosition.current.contains(e.target)
    ) {
      return;
    } else {
      this.closeTooltip();
    }
  };

  getFormatType = (key) => {
    // Return appropriate format type based on the key name
    if (key === "cpc" || key === "cpm") {
      return "currency";
    }
    return "number";
  };

  renderMetrics = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) return null;

    const metricsData = data[0];

    return Object.keys(widgetConfig).map((metric, key) => {
      if (!metricsData[metric]) return null;

      const metricData = {
        value: metricsData[metric].value,
        delta: metricsData[metric].change * 100,
        delta_type:
          metricsData[metric].direction === "up" ? "positive" : "negative",
      };

      return (
        <RenderMetric
          key={metric}
          data={metricData}
          type={widgetConfig[metric].type}
          label={widgetConfig[metric].label}
          format={widgetConfig[metric].format}
          size="md"
        />
      );
    });
  };

  clickBar(d, dataKey) {
    if (this.state.tooltip.show && d.date === this.state.tooltip.date) {
      this.setState({
        tooltip: { ...d, show: false, dataKey },
      });
    } else {
      // close all tooltips from this and other widgets as well
      closeAllBarchartTooltips();
      this.setState({
        tooltip: { ...d, show: true, dataKey, date: d.date },
      });
    }
  }

  render() {
    const { data } = this.props;

    if (!data || !data.data || !Array.isArray(data.data)) {
      return <Error minHeight={400} message="No data available" />;
    }

    return (
      <>
        <div key="metrics" className="row mb-4">
          {this.renderMetrics(data.data)}
        </div>
        <div className="row" ref={this.divPosition}>
          <ResponsiveContainer
            className="legends-non-interactive"
            height={400}
            width="100%"
            ref={(ref) => (this.chartRef = ref)}
          >
            <ComposedChart
              data={data.graph}
              margin={{ top: 12, right: 0, bottom: 0, left: -38 }}
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(this.node, !this.state.tooltip.show)
              }
              isAnimationActive={false}
              onClick={(d) => {
                if (d === null) {
                  this.closeTooltip();
                  return;
                }

                const dataPoint = data.graph.find(
                  (point) => point.date === d.activeLabel
                );

                if (!dataPoint) {
                  console.error(
                    "No data point found for active label:",
                    d.activeLabel
                  );
                  return;
                }

                // Format the date
                const formattedDate = moment(dataPoint.date).format(
                  "MMMM DD, YYYY"
                );

                // Structure the data as an array of metrics
                const extra = [
                  {
                    key: "Evergent New Subscriptions",
                    value: dataPoint.new_subscriptions,
                    color: MAIN_COLORS[0],
                  },
                  {
                    key: "Ad Total Purchases",
                    value: dataPoint.total_purchases,
                    color: MAIN_COLORS[1],
                  },
                  {
                    key: "Ad Network CPA",
                    value: dataPoint.cpa,
                    color: MAIN_COLORS[2],
                  },
                  {
                    key: "Total Starts CPA",
                    value: dataPoint.total_starts_cpa,
                    color: MAIN_COLORS[3],
                  },
                ];

                const activePayload = [
                  {
                    payload: {
                      name: "Metrics",
                      value:
                        dataPoint.new_subscriptions + dataPoint.total_purchases,
                      idx: 0,
                    },
                  },
                ];

                this.setState({
                  tooltip: {
                    ...d,
                    extra,
                    show: true,
                    activeCoordinate: d.activeCoordinate,
                    activePayload,
                    activeLabel: formattedDate,
                    date: formattedDate,
                  },
                });
              }}
            >
              <XAxis
                dataKey="date"
                interval="preserveStartEnd"
                scale="point"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedXAxisTickTimeDay />}
                height={40}
              />

              <YAxis
                yAxisId="left"
                orientation="left"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedYAxisTick />}
                domain={[0, "auto"]}
                width={60}
              />

              <YAxis
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedYAxisTickRightCurrency />}
                domain={[0, "auto"]}
                width={60}
              />

              <Bar
                yAxisId="left"
                dataKey="new_subscriptions"
                fill={MAIN_COLORS[0]}
                barSize={20}
                name="Evergent New Subscriptions"
              />

              <Bar
                yAxisId="left"
                dataKey="total_purchases"
                fill={MAIN_COLORS[1]}
                barSize={20}
                name="Ad Total Purchases"
              />

              <Line
                yAxisId="right"
                type="monotone"
                dataKey="cpa"
                stroke={MAIN_COLORS[2]}
                strokeWidth={2}
                dot={false}
                name="Ad Network CPA"
              />

              <Line
                yAxisId="right"
                type="monotone"
                dataKey="total_starts_cpa"
                stroke={MAIN_COLORS[3]}
                strokeWidth={2}
                dot={false}
                name="Total Starts CPA"
              />

              <Legend verticalAlign="bottom" height={36} />

              {!this.state.tooltip.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);

                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          return (
                            <div className="standard-tooltip">
                              {tooltip_data && tooltip_data.date && (
                                <header className="standard-tooltip__header">
                                  <span className="standard-tooltip__key-header">
                                    Date:
                                  </span>
                                  <span className="standard-tooltip__value-header">
                                    {tooltip_data.date}
                                  </span>
                                </header>
                              )}
                              <main className="standard-tooltip__main">
                                {tooltip_data &&
                                  tooltip_data.new_subscriptions && (
                                    <div className="standard-tooltip__item">
                                      <span className="standard-tooltip__key">
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: MAIN_COLORS[0],
                                          }}
                                        ></span>
                                        Evergent New Subscriptions
                                      </span>
                                      <span className="standard-tooltip__value">
                                        {selectFormat(
                                          "number",
                                          tooltip_data.new_subscriptions
                                        )}
                                      </span>
                                    </div>
                                  )}

                                {tooltip_data &&
                                  tooltip_data.total_purchases && (
                                    <div className="standard-tooltip__item">
                                      <span className="standard-tooltip__key">
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: MAIN_COLORS[1],
                                          }}
                                        ></span>
                                        Ad Total Purchases
                                      </span>
                                      <span className="standard-tooltip__value">
                                        {selectFormat(
                                          "number",
                                          tooltip_data.total_purchases
                                        )}
                                      </span>
                                    </div>
                                  )}

                                {tooltip_data && tooltip_data.cpa && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      <span
                                        className="standard-tooltip__icon"
                                        style={{
                                          background: MAIN_COLORS[2],
                                        }}
                                      ></span>
                                      Ad Network CPA
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "currency",
                                        tooltip_data.cpa
                                      )}
                                    </span>
                                  </div>
                                )}

                                {tooltip_data &&
                                  tooltip_data.total_starts_cpa && (
                                    <div className="standard-tooltip__item">
                                      <span className="standard-tooltip__key">
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: MAIN_COLORS[3],
                                          }}
                                        ></span>
                                        Total Starts CPA
                                      </span>
                                      <span className="standard-tooltip__value">
                                        {selectFormat(
                                          "currency",
                                          tooltip_data.total_starts_cpa
                                        )}
                                      </span>
                                    </div>
                                  )}

                                {this.state.widgetsInsights.length > 0 && (
                                  <InsightsWithinTooltip
                                    data={this.state.widgetsInsights}
                                    tooltipDate={tooltip_data.date}
                                  />
                                )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>

          {this.state.tooltip.activeCoordinate ? (
            <div
              className="xray-tooltip-container"
              style={{
                top: `${this.state.tooltip.activeCoordinate.y}px`,
                left:
                  this.state.tooltip.activeCoordinate.x + 220 < this.state.width
                    ? this.state.tooltip.activeCoordinate.x + 15 + "px"
                    : this.state.tooltip.activeCoordinate.x -
                      getXrayTooltipPosition(
                        this.divPosition.current,
                        this.state.tooltip
                      ) +
                      "px",
                opacity: this.state.tooltip.show ? 1.0 : 0.0,
                visibility: this.state.tooltip.show ? "visible" : "hidden",
                position: "absolute",
                zIndex: 1000,
              }}
            >
              <XRayTooltip
                data={this.state.tooltip}
                close={this.closeTooltip.bind(this)}
                insights={this.state.widgetsInsights}
              />
            </div>
          ) : null}

          <div className="featured-insight-tooltip" ref={this.node}>
            {this.state.widgetsInsights.length > 0 &&
              !this.state.tooltip.show && (
                <RenderTooltipInsights data={this.state.widgetsInsights} />
              )}
          </div>
        </div>
      </>
    );
  }
}

export default GothamPurchasesCPA;
